import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import api from '../../services/api';

// Task 34 — "Yeni Süreç" drawer (POST v1/nine-box/processes). auto → kaynak dönem
// (YALNIZ closed dönemler seçilebilir, BE "talent.ninebox.periodNotClosed" ile reddeder)
// + Sistem A|B seçimi.
//
// TD-13 (Faz 2, 2026-09-11) — FORM TURUNA KAPSAM SEÇİCİ EKLENDİ. Önceki hâlde form dalı
// yalnız bir bilgi metniydi: FE `employeeIds` alanını HİÇ göndermiyor, BE de null gördüğü
// için kiracının TÜM aktif çalışanlarını kapsama alıyordu. Sonuç: İK "yalnız satış ekibi
// için bir tur açayım" diyemiyor; tur, ilgisiz yüzlerce çalışanın yöneticisine form
// gönderiyor ve ya-hep-ya-hiç kapanış kuralıyla birleşince (tek eksik form = tur hiç
// kapanmaz) pratikte kapanamaz hâle geliyordu. Artık kapsam açıkça seçilebilir;
// seçilmezse eski davranış (tüm aktif çalışanlar) KORUNUR.
function buildClosedPeriodAsync() {
  return {
    onSearch: async (q) => {
      const params = q
        ? { 'name[like]': q, 'status[eq]': 'closed', pageSize: 20 }
        : { 'status[eq]': 'closed', pageSize: 20 };
      const res = await api.get('/v1/evaluation-periods', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((p) => ({ id: p.id, name: p.name }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/evaluation-periods/${id}`);
      const p = res.data?.data;
      return p ? { id: p.id, name: p.name } : null;
    },
  };
}

const closedPeriodAsync = buildClosedPeriodAsync();

// TD-13 — kapsam seçici veri kaynağı. onGetById ŞART: çekmece yeniden açıldığında
// seçili çipler boş görünmemeli (PMF-12'nin "tıklamak siliyor" kusuru aynı aileden).
function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q
        ? { 'fullName[like]': q, pageSize: 20 }
        : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() } : null;
    },
  };
}

const employeeAsync = buildEmployeeAsync();

export default function ProcessStartDrawer({ open, onClose, onSave }) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [xSource, setXSource] = useState('auto');
  const [sourcePeriodId, setSourcePeriodId] = useState(null);
  const [sourceSystem, setSourceSystem] = useState('A');
  // TD-13 — boş dizi = "kapsam seçilmedi" → BE'ye alan HİÇ gönderilmez (tüm aktif
  // çalışanlar). Dolu dizi yalnız seçilenleri kapsar.
  const [scopeEmployeeIds, setScopeEmployeeIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const systemOptions = [
    { id: 'A', name: t('performance.ninebox.process.sourceSystemA') },
    { id: 'B', name: t('performance.ninebox.process.sourceSystemB') },
  ];

  useEffect(() => {
    if (open) {
      setName('');
      setXSource('auto');
      setSourcePeriodId(null);
      setSourceSystem('A');
      setScopeEmployeeIds([]);
      setSaving(false);
    }
  }, [open]);

  const canSave = name.trim().length > 0 && (xSource === 'form' || !!sourcePeriodId) && !saving;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    const payload = xSource === 'auto'
      ? { name: name.trim(), xSource: 'auto', sourcePeriodId, sourceSystem }
      // Kapsam seçilmediyse alan GÖNDERİLMEZ: BE'de `null` (tüm aktif çalışanlar) ile
      // boş dizi (kasıtlı boş kapsam) FARKLI anlamlar taşır, karıştırılamaz.
      : {
        name: name.trim(),
        xSource: 'form',
        ...(scopeEmployeeIds.length > 0 ? { employeeIds: scopeEmployeeIds } : {}),
      };
    const ok = await onSave(payload);
    if (ok) onClose();
    setSaving(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="420px"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
        margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
      }}
    >
      <Header>
        <Title>{t('performance.ninebox.process.new')}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        <TextInput
          label={t('performance.ninebox.process.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <RadioGroupWrap>
          <RadioGroupLabel>{t('performance.ninebox.process.source')}</RadioGroupLabel>
          <RadioRow role="radiogroup">
            <RadioLabel>
              <RadioInput
                type="radio"
                name="ninebox-xsource"
                checked={xSource === 'auto'}
                onChange={() => setXSource('auto')}
              />
              {t('performance.ninebox.process.sourceAuto')}
            </RadioLabel>
            <RadioLabel>
              <RadioInput
                type="radio"
                name="ninebox-xsource"
                checked={xSource === 'form'}
                onChange={() => setXSource('form')}
              />
              {t('performance.ninebox.process.sourceForm')}
            </RadioLabel>
          </RadioRow>
        </RadioGroupWrap>

        {xSource === 'auto' ? (
          <>
            <AsyncGenericSelect
              label={t('performance.ninebox.process.sourcePeriod')}
              value={sourcePeriodId}
              onChange={setSourcePeriodId}
              onSearch={closedPeriodAsync.onSearch}
              onGetById={closedPeriodAsync.onGetById}
              required
            />
            <GenericSelect
              label={t('performance.ninebox.process.sourceSystem')}
              value={sourceSystem}
              onChange={setSourceSystem}
              options={systemOptions}
            />
          </>
        ) : (
          <>
            <AsyncGenericSelect
              label={t('performance.ninebox.process.scope')}
              value={scopeEmployeeIds}
              onChange={setScopeEmployeeIds}
              onSearch={employeeAsync.onSearch}
              onGetById={employeeAsync.onGetById}
              isMulti
            />
            <InfoText>
              {scopeEmployeeIds.length > 0
                ? t('performance.ninebox.process.formInfoScoped', { count: scopeEmployeeIds.length })
                : t('performance.ninebox.process.formInfo')}
            </InfoText>
          </>
        )}
      </Body>

      <Footer>
        <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={!canSave} loading={saving}>{t('common.save')}</Button>
      </Footer>
    </Drawer>
  );
}

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const Title = styled.h3`
  font-size: 16px; font-weight: 700; color: ${({ theme }) => theme.colors.text.primary}; font-family: 'Nunito Sans', sans-serif; margin: 0;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;
const Body = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 18px;
`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;
const RadioGroupWrap = styled.div``;
const RadioGroupLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 8px;
`;
const RadioRow = styled.div`
  display: flex;
  gap: 18px;
`;
const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
`;
const InfoText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  padding: 12px 14px;
  line-height: 1.5;
`;
